import axios from 'axios';
import Vue from 'vue';
import { isProductType, getMediaTypeGroup } from '@motionelements/core/src/services/catalog.service.js';
import { parseSearchKeyword } from '@motionelements/core/src/helpers/search.js';
import featureFlagMixin, { isFeatureEnabled } from '@motionelements/core/src/components/mixins/feature-flag.mixin.js';
import {
  hasMediaUserGesture,
  isHoverPreviewToolbarMuted,
} from '@motionelements/helper/player/nativeVideo';
import * as searchApi from '@/api/search.js';

const hasOwnProp = (obj, prop) => Object.prototype.hasOwnProperty.call(obj, prop);

const state = {
  default: {
    filter: {
      productModal: 0,
      similar: '',
    },
    similar: {
      imageUrl: '',
      imageMeta: null,
    },
  },

  // move out from filter.search
  keyword: '',
  cachedKeyword: null,

  /** Page ID when URL has discovery_page=… (analytics / channel switch). Optional leading "d"/"D" is stripped; prefix-only or empty → null. */
  discoveryPageId: null,

  terms: {},

  mediaType: null,

  // filter mapping list
  isMediaTypeTermsLoaded: false,

  // @deprecated
  licenses: {},

  elements: [],
  asideElements: {},
  crossSellElements: {},

  typeahead: {
    keywords: [],
  },
  // popularCategories: [],
  // hasSuggestionKeyword: false,
  // hasSuggestionArtist: false,
  suggestion: {
    keywords: [],
    artists: [],
  },
  // no-results suggestions
  suggested: {
    keywords: null,
    artists: null,
  },

  noResultsStatus: 0,

  mediaTypeTabs: [
    {
      id: '',
      name: 'All',
      count: 0,
    },
  ],
  sort: 'popular',
  testSort: null,
  facets: {},
  filter: {},
  similar: {
    imageUrl: '',
    imageMeta: null,
  },
  similarFaces: [],
  error: {
    code: '',
    message: '',
  },

  // https://github.com/axios/axios/issues/1361#issuecomment-366807250
  // to cancel old search request to prevent race condition
  cancelTokens: {},

  // for tracking
  queryId: null,

  pagination: {
    totalCount: 0,
    currentPage: 1,
    // perPage: 50,
  },

  // preferences
  preferences: {
    showName: false,
    showCategory: true,
    showPrice: true,
    perPage: 50,
    videoPlayer: {
      muted: false,
    },
  },
  // Autoplay policy only — not persisted; see hoverPreviewMediaUserGesture getter
  hoverPreviewMediaUserGesture: false,
  perPageItems: [10, 50, 100, 200, 500],

  isLoading: false,
  isMobileFacetsActive: false,

  // track fileSearch upload
  upload: {
    status: false,
    message: '',
    progress: 0,
  },

  // experiments: [],
  // experimentVariant: null,

  similarToProduct: null,
  similarGroups: [],
  //
  tokens: [],
};

const getters = {
  mediaTypeTabs: state => state.mediaTypeTabs,
  allSearchElements: state => state.elements,
  allSearchAsideElements: state => state.asideElements,
  allSearchCrossSellElements: state => state.crossSellElements,
  hasAsideElements: state => _.size(state.asideElements) > 0,
  hasCrossSellElements: state => _.size(state.crossSellElements) > 0,
  allSearchFilter: state => state.filter,
  allSearchFacets: state => state.facets,
  // easy access getters
  isProductType: (state) => (productType) => isProductType(productType, state.mediaType),
  getMediaTypeGroup: state => getMediaTypeGroup(state.mediaType),
  // getExperimentVariantId: (state) => (experimentId) => state.experiments.find(x => x.id === experimentId) || null,
  sort: state => state.sort,
  keyword: state => state.keyword,
  discoveryPageId: state => state.discoveryPageId,
  // facetAttribute: state => state.facets.attribute,
  // facetSizes: state => state.facets.size,
  facetMediaType: state => state.facets.mt,
  // facetEditable: state => state.facets.editable,
  // facetTutorial: state => state.facets.tutorial,
  // facetPlugin: state => state.facets.plugin,
  // facetSkill: state => state.facets.skill,
  // facetTextspace: state => state.facets.textspace,
  // facetAlpha: state => state.facets.alpha,
  // facetLoop: state => state.facets.loop,
  facetVersions: state => state.facets.software_version,
  facetOrientations: state => state.facets.orientation,
  facetResolutions: state => state.facets.resolution,
  facetFps: state => state.facets.fps,
  facetTypes: state => state.facets.type,
  facetSizes: state => state.facets.size,
  facetCategories: state => state.facets.category,
  facetStyles: state => state.facets.style,
  // facetColor: state => state.facets.style,
  facetGenres: state => state.facets.genre,
  facetInstruments: state => state.facets.instrument,
  facetMoods: state => state.facets.mood,
  facetDurationRange: state => state.facets.duration_range,
  facetFramings: state => state.facets.framing,
  facetBpmRange: state => state.facets.bpm_range,
  facetPriceRange: state => state.facets.price_range,
  facetLicenses: state => state.facets.license,
  perPage: state => state.preferences.perPage,
  currentPage: state => state.pagination.currentPage,
  totalCount: state => state.pagination.totalCount,
  totalPageCount: state => Math.ceil(state.pagination.totalRows / state.preferences.perPage),
  videoPlayerMuted: state => state.preferences.videoPlayer.muted,
  hoverPreviewMediaUserGesture: (state) => (
    state.hoverPreviewMediaUserGesture || hasMediaUserGesture()
  ),
  hoverPreviewToolbarMuted: (state, getters) => isHoverPreviewToolbarMuted(
    getters.videoPlayerMuted,
    getters.hoverPreviewMediaUserGesture,
  ),
  //
  isInfiniteScrollMode(state, getters, rootState) {
    return featureFlagMixin.methods.isFeatureEnabled('search_infinite_scroll')
      && isProductType(['image', 'video'], state.mediaType)
      && _.get(rootState, 'route.route.name') === 'view-search-list';
  },
  hasSuggestions(state) {
    return _.size(state.suggestion.keywords) || _.size(state.suggestion.artists);
  },
  appliedFilterCount(state) {
    const filters = { ...state.filter };
    delete filters.mediaType;

    if (_.get(filters, 'subscription') === -1) {
      delete filters.subscription;
    }

    return _.size(_.filter(filters, v => v && v !== '0'));
  },
};

function buildSearchParams(state) {
  const filter = state.filter;
  const pagination = state.pagination;
  const sort = state.sort;

  // use new sort
  delete filter.sort;
  delete filter.inputSort;

  const params = {
    page: pagination.currentPage || 1,
    perPage: state.preferences.perPage || 50,
    sort: sort,
    facetarray: 1, // return facets in array, to prevent camelCaseKeys issue
  };

  if (state.testSort) {
    params.testSort = state.testSort;
  }

  if (state.keyword) {
    params.q = state.suggested.keywords ? state.suggested.keywords : state.keyword;
  }

  if (state.keyword.length && !sort) {
    params.sort = 'match';
  }

  if (filter.subscription === 1) {
    params.subscription = 1;
  }

  if (filter.category) {
    params.category = filter.category;
  }

  if (filter.generator) {
    params.generator = filter.generator;
  }

  if (filter.bpmRange) {
    params.tempo = filter.bpmRange;
  }

  if (filter.durationRange) {
    params.duration = filter.durationRange;
  }

  if (filter.semanticRatio) {
    params.semanticRatio = filter.semanticRatio;
  }

  const validKeys = [
    'editable', 'tutorial', 'plugin',
    'skill', 'textspace', 'alpha', 'loop',
    'vector', 'layered', 'aiGenerated',

    'color', 'colorSpace',
    'orientation', 'resolution', 'fps',
    'type', 'size', 'style',

    // music
    'genre', 'instrument', 'mood',
    'youtubeContentId',

    'duration', 'framing',
    'license', 'by', // included artists
    '-by', // excluded artists

    'similar', 'similarType', 'similarFaceId', 'priceRange', 'softwareVersion', 'program',

    // 3d
    'polygon', 'format',
    'source',

    // ae
    'plugin',
    'noPlugin',
    'generator',
  ];

  // const params = new URLSearchParams();
  Object.keys(filter).forEach((key) => {
    if (validKeys.includes(key)) {
      const value = filter[key];

      // console.log(key, value);
      switch (key) {
        case 'noPlugin':
          if (value) {
            params.plugin = -1;
          }
          break;

        default:
          if (value || value === false) {
            params[key] = value;
          }
      }
    }
  });

  // if (filter.color) {
  //   params.similar = filter.color;
  // }

  // keep "by" as front-end query param but send param "contributor" to API instead "by" or "artist"
  let includedArtists = [];
  let excludedArtists = [];
  if (_.has(params, 'by')) { // keep "by" as front-end query param but send param "contributor" to API
    // params.contributor = params.by;
    includedArtists = params.by.split(',');
    delete params.by;
  }
  if (_.has(params, '-by')) { // keep "by" as front-end query param but send param "contributor" to API
    // params.contributor = _.get(params, 'contributor', '').concat(params['-by']);
    excludedArtists = params['-by'].split(',');
    delete params['-by'];
  }
  const contributors = [...includedArtists, ...excludedArtists];
  if (contributors.length) params.contributor = contributors.join(',');

  if (_.has(params, 'artist')) { // keep "by" as front-end query param but send param "contributor" to API
    params.contributor = params.artist;
    delete params.artist;
  }
  return params;
}

/*
function tidySearchResultFacets(facets) {
 // console.log('tidySearchResult', result);
 // const currentMediaType = store.getters['site/currentMediaType'];
 // console.log('tidySearchResult mapVersions:', currentMediaType.mainMediaType);


 if (_.has(facets, 'softwareVersion')) {
   // set software_version
   const map = store.getters['search/getFieldSchema']('softwareVersion');

   if (map) {
     facets.softwareVersion.forEach((data) => {
       const schema = map.find(x => x.version === data.value);

       if (schema) {
         // old API
         data.name = schema.display_version;
       }
     });
   } else {
     console.error('MAP not found', 'softwareVersion');
   }
 }

 // eslint-disable-next-line no-plusplus
 ['type', 'genre', 'instrument', 'category', 'style', 'mood'].forEach((field) => {
   if (_.has(facets, field)) {
     // set software_version
     const map = store.getters['search/getFieldSchema'](field);

     if (map) {
       facets[field].forEach((data) => {
         const schema = map.find(x => x.id === data.value.toString());

         if (schema) {
           data.name = schema.name;
         }
       });
     } else {
       console.error('MAP not found', field);
     }
   }
 });

 // ['durationRange', 'bpmRange', 'priceRange'].forEach((field) => {
 //   if (_.has(facets, field)) {
 //     facets[field].forEach((data) => {
 //       const [min, max] = data.value;
 //       data.min = min;
 //       data.max = max;
 //     });
 //   }
 // });

 return facets;


   Object.keys(facets.softwareVersion).map((versionId) => {
     const data = _.get(facets.softwareVersion, versionId);

     // const hasValue = _.has(facets.softwareVersion, versionId) ? true : false;

     if (data) {
       const count = this.itemCountCheck(facets.softwareVersion[versionId]);
       const mapKey = _.findKey(mapVersions, { version: versionId });
       // console.log('software_version', versionId, mapKey);

       if (mapKey) {
         const name = mapVersions[mapKey].displayVersion;

         // only show version number when is resolve
         let textValue = this.$t('search.{version}_and_below', { version: name });
         if (currentMediaType.mainMediaType === 'resolve') {
           textValue = name;
         }

         const info = {
           value: mapKey,
           text: textValue,
           name: name,
           count: count,
         };

         facets.softwareVersion[versionId] = info;
       }

       // remove filter if not on api mapping list
       if (mapKey === undefined) {
         delete result.data.facets.softwareVersion[versionId];
       }
     }
   });
 }


 // set fps
 if (_.has(facets, 'fps')) {
   Object.keys(result.data.facets.fps).map((fpsId) => {
     const hasValue = _.has(result.data.facets.fps, fpsId) ? true : false;
     if (hasValue) {
       const count = this.itemCountCheck(result.data.facets.fps[fpsId]);

       const info = {
         value: fpsId,
         name: fpsId,
         count: count,
       };
       result.data.facets.fps[fpsId] = info;
     }
   });
 }


 // set price range and calculation currency
 const mapCurrencies = this.$store.getters['site/mapCurrencies'];
 const currentCurrency = this.$store.getters['site/currentCurrency'];

 console.log('tidySearchResult', mapCurrencies);
 console.log('tidySearchResult', currentCurrency);

 const currencyInfo = (mapCurrencies[currentCurrency]) || mapCurrencies.USD;

 console.log('tidySearchResult', currencyInfo);

 if (_.has(facets, 'priceRange')) {
   Object.keys(result.data.facets.price_range).map((priceString) => {
     if (!_.size(priceString)) {
       return;
     }

     let info = {};
     const count = this.itemCountCheck(result.data.facets.price_range[priceString]);

     if (priceString === '-1') {
       info = {
         value: '-1',
         text: this.$t('search.price_free'),
         low: '',
         high: '',
         count: count,
         symbol: '',
       };
     }

     if (priceString !== '-1') {
       const priceLow = priceString.split('-')[0];
       const priceHigh = priceString.split('-')[1];

       const isOnePrice = priceHigh === undefined;

       const symbol = currencyInfo.symbol;


       // console.log('facets.price_range', priceString, currencyInfo);

       let currencyText = '';
       if (isOnePrice) {
         currencyText = `${symbol} ${(priceLow * currencyInfo.rate)}`;
       } else {
         const priceLowString = `${symbol}${(priceLow * currencyInfo.rate)}`;
         const priceHighString = `${symbol}${(priceHigh * currencyInfo.rate)}`;
         currencyText = `${priceLowString} - ${priceHighString}`;

         // Less then US$ 20
         if (priceLow === '*') {
           currencyText = this.$t('search.less_than', { '0': priceHighString });
         }

         // US$ 40 or more
         if (priceHigh === '*') {
           currencyText = this.$t('search.or_more', { '0': priceLowString });
         }
       }

       info = {
         value: priceString,
         text: currencyText,
         low: priceLow,
         high: priceHigh,
         count: count,
         symbol: currencyInfo.symbol,
       };
     }

     // console.log(info);
     result.data.facets.price_range[priceString] = info;
   });
 }

 // set duration range
 if (_.has(facets, 'durationRange')) {
   Object.keys(result.data.facets.duration_range).map((durationString) => {
     if (_.size(durationString)) {
       const count = this.itemCountCheck(result.data.facets.duration_range[durationString]);

       const stringSeconds = this.$t('search.seconds');

       const durationLow = durationString.split('-')[0];
       const durationHigh = durationString.split('-')[1];

       const durationHighString = `${durationHigh}${stringSeconds}`;
       const durationLowString = `${durationLow}${stringSeconds}`;

       let durationText = `${durationLowString} - ${durationHighString}`;

       // Shorter than 20s
       if (durationLow === '*') {
         // durationText = `${stringShorter} ${durationHigh}${stringSeconds}`;
         durationText = this.$t('search.less_than', { '0': durationHighString });
       }

       // 120s or longer
       if (durationHigh === '*') {
         // durationText = `${durationLow}${stringSeconds} ${stringLonger} `;
         durationText = this.$t('search.or_more', { '0': durationLowString });
       }

       const info = {
         value: durationString,
         text: durationText,
         low: durationLow,
         high: durationHigh,
         count: count,
       };

       result.data.facets.duration_range[durationString] = info;
     }
   });
 }


 // set bpm range
 if (_.has(facets, 'bpmRange')) {
   Object.keys(result.data.facets.bpm_range).map((bpmString) => {
     if (_.size(bpmString)) {
       const count = this.itemCountCheck(result.data.facets.bpm_range[bpmString]);

       const bpmLow = bpmString.split(',')[0];
       const bpmHigh = bpmString.split(',')[1];

       let bpmText = '';
       switch (bpmString) {
         case '0,80':
           bpmText = this.$t('search.slow');
           break;
         case '70,110':
           bpmText = this.$t('search.moderate');
           break;
         case '110,150':
           bpmText = this.$t('search.fast');
           break;
         case '140,*':
           bpmText = this.$t('search.vary_fast');
           break;
         default:
           break;
       }

       const info = {
         value: bpmString,
         text: bpmText,
         low: bpmLow,
         high: bpmHigh,
         count: count,
       };

       result.data.facets.bpm_range[bpmString] = info;
     }
   });
 }

 // set license
 if (_.has(facets, 'license')) {
   Object.keys(result.data.facets.license).map((licenseId) => {
     const hasValue = _.has(result.data.facets.license, licenseId) ? true : false;
     if (hasValue) {
       const count = this.itemCountCheck(result.data.facets.license[licenseId]);

       let name = '';
       switch (licenseId) {
         case 'rf':
           name = this.$t('search.royalty_free_rf');
           break;
         case 'pro':
           name = this.$t('search.pro_registered');
           break;
         case 'editorial':
           name = this.$t('search.editorial');
           break;
         default:
           break;
       }

       const info = {
         value: licenseId,
         name: name,
         count: count,
       };
       result.data.facets.license[licenseId] = info;
     }
   });
 }


 // calculation colletcion's element currency
 // if (result.data.data.collections) {
 //   Object.keys(result.data.data.collections).map((collectionKey) => {
 //     const hasValue = _.has(result.data.data.collections, collectionKey) ? true : false;
 //     if (hasValue) {
 //       const price = result.data.data.collections[collectionKey].price;
 //       const priceOriginal = result.data.data.collections[collectionKey].price_original;

 //       result.data.data.collections[collectionKey].price = (price * currencyInfo.rate);
 //       result.data.data.collections[collectionKey].price_original = (priceOriginal * currencyInfo.rate);
 //     }
 //   });
 // }

 return facets;


}

  */

function refreshCancelToken(state, tokenName) {
  // cancel previous request
  if (_.has(state.cancelTokens, tokenName)) {
    state.cancelTokens[tokenName].cancel();
  }

  // create cancel token
  state.cancelTokens[tokenName] = axios.CancelToken.source();

  return state.cancelTokens[tokenName];
}

const actions = {
  // listPopularCategories({ commit, state }, payload) {
  //   listMediaTypeCategories(payload.mediaType, {
  //     'fields[category]': 'name',
  //     sort: 'popular',
  //     per_page: 4,
  //     type: _.get(payload, 'type'),
  //   })
  //     .then(({ data: { data: categories } }) => {
  //       // commit('setPopularCategories', categories.filter(obj => obj.type === 'category'));
  //       commit('setPopularCategories', categories);
  //     });
  // },

  setPagination({ commit }, payload) {
    if (_.get(payload, 'page') > 0) {
      commit('setCurrentPage', payload.page);
    }
    if (_.get(payload, 'perPage') > 0) {
      commit('setPerPage', payload.perPage);
    }
  },
  listAutoCompleteKeywords({ state, commit }, text) {
    text = parseSearchKeyword(text).keyword;
    const params = {
      q: text,
    };

    // clear first
    commit('setTypeaheads', []);
    return searchApi.listAutoCompleteKeywords(state.mediaType, params).then((response) => {
      // /suggest response is not wrapped in data.data
      const data = response.data;
      commit('setTypeaheads', data);
      return response;
    });
  },
  changeMediaType({ state, commit }, mediaType) {
    const { filter } = state;

    // this.$store.dispatch('site/setLoadingSearch', { status: true });

    // let filter = this.$store.getters['search/allSearchFilter'];
    // const keyword = state.keyword;
    const program = filter.program;
    // const keyword = filter.searchKeyword;
    const subscription = filter.subscription;
    const by = filter.by;
    const priceRange = filter.priceRange;
    const productModal = filter.productModal;

    // reset all filter
    commit('setElements', []);
    commit('initFilter');
    commit('resetSimilar');

    // set back searching keyword
    // filter = this.$store.getters['search/allSearchFilter'];
    filter.program = program;
    filter.subscription = subscription;
    // filter.searchKeyword = keyword;
    filter.by = by;
    filter.priceRange = priceRange;
    filter.productModal = productModal;

    commit('setFilter', filter);

    if (_.get(filter, 'page') > 0) {
      commit('setCurrentPage', filter.page, 10);
    }

    if (_.get(filter, 'itemPerPage') > 0) {
      commit('setPerPage', filter.itemPerPage);
    }

    // this.$store.dispatch('search/setFilter', filter);

    // const lang = this.$store.getters['site/currentLanguage'];
    // this.$router.push({
    //   path: `/${lang}/search/${mediaType}`,
    // });
    commit('setMediaType', mediaType);
  },
  changeSubMediaType({ state, commit }, mediaType) {
    const { filter } = state;


    // get current filter
    // const keyword = filter.searchKeyword;
    const sort = filter.sort;
    const subscription = filter.subscription;
    const by = filter.by;
    const priceRange = filter.priceRange;
    const productModal = filter.productModal;

    // reset all filter
    // reset all filter
    commit('setElements', []);
    commit('initFilter');
    commit('resetSimilar');

    // set back filter
    // filter.searchKeyword = keyword;
    filter.sort = sort;
    filter.subscription = subscription;
    filter.by = by;
    filter.priceRange = priceRange;
    filter.productModal = productModal;

    commit('setFilter', filter);

    commit('setMediaType', mediaType);
  },
  searchSimilarElements({ state }, parameters) {
    const params = { ...parameters, ...buildSearchParams(state) };
    return searchApi.searchElements(state.mediaType, params);
  },
  // eslint-disable-next-line no-unused-vars
  // search for elements
  searchElements({ state, commit }, options) {
    // const { filter, pagination } = state;
    // const { site } = rootState;
    let params = buildSearchParams(state);
    /*
    // options only for experiments
    if (_.has(options, 'experimentId') && _.has(options, 'experimentVariant')) {
      params.experimentId = options.experimentId;
      params.experimentVariant = options.experimentVariant;
    }
     */
    if (hasOwnProp(options, 'params')) params = { ...params, ...options.params };
    if (params.page === 1) {
      commit('searchInfiniteScroll/setAiBannerShown', false, { root: true });
    }
    const tokenName = `searchElements-${params.page}-${options.params.semantic_ratio}`;
    // const cancelToken = refreshCancelToken(state, tokenName);
    commit('refreshToken', `searchElements-${params.page}-${options.params.semantic_ratio}`);
    const cancelToken = state.tokens.find(({ tokenName: id }) => id === tokenName);
    return searchApi.searchElements(state.mediaType, params, {
      cancelToken: cancelToken.token,
    })
      .then((response) => {
        const data = response.data;
        if (!options.infiniteScroll) commit('setElementsResult', { ...data, infiniteScroll: options.infiniteScroll });
        commit('setLoading', false);
        return response;
      })
      .catch((e) => {
        // handle canceled by cancelToken
        if (axios.isCancel(e)) {
          console.log('request canceled', e);
          throw e;
        } else {
          commit('setLoading', false);
          if (!_.get(options, 'infiniteScroll')) commit('setElementsResult', { data: [] });
          throw e;
        }
      });
  },
  /*
  // eslint-disable-next-line no-unused-vars
  getSearchResult({ state, commit, rootState }) {
    const { filter } = state;
    const { site } = rootState;

    const params = new URLSearchParams();
    params.append('language', site.currentLanguage);
    params.append('page', filter.page || 1);
    params.append('per_page', filter.itemPerPage || 50);
    params.append('sort', filter.sort || 'match');
    // params.append('sort_by', filter.sortBy);
    // params.append('sort_order', filter.sortOrder);

    params.append('q', filter.searchKeyword);
    params.append('mt', filter.mediaType);

    params.append('program', filter.program);

    if (filter.subscription !== '-1') {
      params.append('subscription', filter.subscription);
    }

    params.append('editable', filter.editable);
    params.append('tutorial', filter.tutorial);
    params.append('plugin', filter.plugin);
    params.append('skill', filter.skill);
    params.append('textspace', filter.textspace);
    params.append('alpha', filter.alpha);
    params.append('loop', filter.loop);

    if (filter.softwareVersion) {
      params.append('software_version', filter.softwareVersion);
    }

    params.append('orientation', filter.orientation);
    params.append('resolution', filter.resolution);
    params.append('fps', filter.fps);
    params.append('type', filter.type);
    params.append('size', filter.size);
    params.append('cat', filter.category);
    params.append('style', filter.style);
    params.append('color', filter.color);

    params.append('genre', filter.genre);
    params.append('instrument', filter.instrument);
    params.append('mood', filter.mood);

    params.append('duration', filter.durationRange);
    params.append('framing', filter.framing);
    params.append('tempo', filter.bpmRange);
    params.append('price_range', filter.priceRange);
    params.append('license', filter.license);

    params.append('artist', filter.artist || '');

    params.append('similar', filter.similar);
    if (filter.color) {
      params.append('similar', filter.color);
    }

    return api.getSearchResult(params);
  },
  */
  listSuggestions({ state, commit }) {
    // const { filter } = state;
    const params = {
      q: state.keyword,
    };
    // cancel previous request
    if (state.listSuggestionsCancelToken) {
      state.listSuggestionsCancelToken.cancel();
    }
    // create cancel token
    const cancelToken = refreshCancelToken(state, 'listSuggestions');
    return searchApi.listSuggestions(state.mediaType, params, {
      cancelToken: cancelToken.token,
    })
      .then((response) => {
        const data = response.data.data;
        commit('setSuggestions', data);
      })
      .catch((e) => {
        // handle canceled by cancelToken
        if (axios.isCancel(e)) {
          console.log('request canceled', e);
        }
      });
  },
  // eslint-disable-next-line no-unused-vars
  getSearchPaidResult({ state, commit }) {
    // const { filter } = state;
    const { pagination, sort, preferences } = state;
    // const { site } = rootState;

    // const itemPerPage = (filter.itemPerPage) * 0.2;
    const itemPerPage = preferences.perPage * 0.2;

    const params = buildSearchParams(state);

    params.page = pagination.currentPage || 1;
    params.perPage = itemPerPage;
    params.sort = sort || 'match';

    // overwrite
    delete params.subscription;
    params.priceRange = '1-*';

    const cancelToken = refreshCancelToken(state, 'getSearchPaidResult');

    return searchApi.searchElements(state.mediaType, params, {
      cancelToken: cancelToken.token,
    }).then((response) => {
      // console.log('getSearchPaidResult', response);
      commit('setSearchPaidResult', response.data.data);
    }).catch((e) => {
      // handle canceled by cancelToken
      if (axios.isCancel(e)) {
        console.log('request canceled', e);
      }
    });
  },
  // eslint-disable-next-line no-unused-vars
  getSearchCrossSellResult({ state, commit }) {
    const { filter } = state;
    // const { site } = rootState;

    const params = buildSearchParams(state);

    params.page = filter.page || 1;
    params.perPage = 4;
    params.sort = filter.sort || 'match';

    // overwrite
    params.subscription = '-1';
    params.priceRange = '1-*';

    const cancelToken = refreshCancelToken(state, 'getSearchCrossSellResult');

    return searchApi.searchElements(state.mediaType, params, {
      cancelToken: cancelToken.token,
    }).then((response) => {
      // console.log('getSearchCrossSellResult', response);
      commit('setSearchCrossSellResult', response.data.data);
    }).catch((e) => {
      // handle canceled by cancelToken
      if (axios.isCancel(e)) {
        console.log('request canceled', e);
      }
    });
    // return api.getSearchResult(params);
  },

  setFilter({ commit }, filters) {
    // fix key to int
    Object.keys(filters).forEach((field) => {
      if (['subscription'].includes(field)) {
        filters[field] = parseInt(filters[field], 10);
      }
    });
    commit('setCurrentPage', 1);
    // filter = filter || {};
    // filter.page = 1;
    commit('setFilter', filters);
  },
  setFilters({ commit }, filters) {
    // filters = filters || {};

    if (filters) {
      const field = 'subscription';
      // fix key to int
      if (filters[field] !== undefined) {
        filters[field] = parseInt(filters[field], 10);
      }
    } else {
      filters = {};
    }

    // fix key to int
    // Object.keys(filters).forEach((field) => {
    //   if (['subscription'].includes(field)) {
    //     filters[field] = parseInt(filters[field], 10);
    //   } else if (['priceRange'].includes(field)) {
    //     filters[field] = filters[field].toString();
    //   }
    // });
    // filter valid filters
    commit('setFilters', filters);
  },
  setKeyword({ commit }, keyword) {
    commit('setCurrentPage', 1);
    commit('setKeyword', keyword);
  },
};

const mutations = {
  setSimilarToProduct(state, product) {
    state.similarToProduct = product;
  },
  setSimilarGroups(state, similarGroups) {
    state.similarGroups = similarGroups;
  },
  // preferences
  setPreferences(state, { key, value }) {
    state.preferences[key] = value;
  },
  // pagination
  setCurrentPage(state, number) {
    state.pagination.currentPage = parseInt(number, 10);
  },
  setPerPage(state, number) {
    state.preferences.perPage = parseInt(number, 10);
  },
  setVideoPlayerMuted(state, bool) {
    state.preferences.videoPlayer.muted = bool;
  },
  setHoverPreviewMediaUserGesture(state, bool) {
    state.hoverPreviewMediaUserGesture = bool;
  },
  setSort(state, payload) {
    // cannot sort by match if no keyword
    if (payload === 'match' && !state.keyword) return;
    state.sort = payload;
    // console.log('setSort', payload);
  },
  setTestSort(state, testSort) {
    state.testSort = testSort;
  },
  initFilter() {
    state.filter = JSON.parse(JSON.stringify(state.default.filter));
  },
  resetFilter() {
    // console.log('resetFilter');
    const defaultFilter = JSON.parse(JSON.stringify(state.default.filter));
    Object.keys(defaultFilter).map((filterName) => {
      const ignoreFacets = [
        'sortBy',
        'sortOrder',
        'inputSort',
        'itemPerPage',
      ];

      if (ignoreFacets.indexOf(filterName) >= 0) {
        return;
      }

      if (state.filter[filterName]) {
        // console.log('resetFilter - ', filterName, state.filter[filterName]);
        state.filter[filterName] = defaultFilter.filterName;
      }
    });
  },
  resetFilters() {
    // console.log('resetFilter');
    const defaultFilter = JSON.parse(JSON.stringify(state.default.filter));
    Object.keys(defaultFilter).map((filterName) => {
      if (state.filter[filterName]) {
        // console.log('resetFilter - ', filterName, state.filter[filterName]);
        state.filter[filterName] = defaultFilter.filterName;
      }
    });
  },
  resetSimilar() {
    // console.log('resetSimilar');
    state.similar = JSON.parse(JSON.stringify(state.default.similar));
  },
  // setLoading(state, status) {
  //   state.isLoading = status;
  // },
  setMediaType(state, mediaType) {
    state.mediaType = mediaType.replace('-', '_');
  },
  setMediaTypeTabs(state, info) {
    state.mediaTypeTabs = info;
  },
  setTypeaheads(state, typeaheads) {
    state.typeahead.keywords = typeaheads;
  },
  // setPopularCategories(state, categories) {
  //   state.popularCategories = categories;
  // },
  setElementsResult(state, result) {
    console.log(isFeatureEnabled('product_pop-up_modal'));
    console.log('========== setElementsResult ', result.data);
    // if (isFeatureEnabled('product_pop-up_modal')) {
    //   result.data.forEach((element, index) => {
    //     const prevElement = result.data[index - 1];
    //     const nextElement = result.data[index + 1];
    //     element.prevProductId = _.get(prevElement, 'id') || null;
    //     element.nextProductId = _.get(nextElement, 'id') || null;
    //   });
    // }
    state.elements = result.data;
    // state.filter.page = result.page;
    // state.filter.pageTotal = result.totalCount;
    // state.filter.itemPerPage = result.perPage;
    state.facets = result.facets;
    state.similarFaces = (result.faces && result.faces.length) ? result.faces : [];
    // set pagination
    if (result.page) state.pagination.currentPage = result.page;
    if (result.perPage && result.infiniteScroll) state.preferences.perPage = result.perPage;
    state.pagination.totalCount = result.totalCount ? result.totalCount : 0;
  },
  setFacets(state, facets) {
    state.facets = facets;
  },
  setSimilarFaces(state, faces) {
    state.facets = faces;
  },
  setSuggestions(state, suggestions) {
    state.suggestion = {
      keywords: _.get(suggestions, 'keywords', []),
      artists: _.get(suggestions, 'artists', []),
      free: _.get(suggestions, 'free', false),
    };
  },
  setArtistSuggestions(state, artistSuggestions) {
    state.suggestion.artists = artistSuggestions;
  },
  clearSuggestions(state) {
    state.suggestion = {
      keywords: [],
      artists: [],
      free: false,
    };
  },
  setSearchPaidResult(state, elements) {
    state.asideElements = elements;
  },
  setSearchCrossSellResult(state, elements) {
    state.crossSellElements = elements;
  },
  setElements(state, elements) {
    state.elements = elements;
  },
  setAsideElements(state, elements) {
    state.asideElements = elements;
  },
  setLicense(state, license) {
    state.licenses = license;
  },
  setTerms(state, result) {
    state.terms = result.data;
  },
  setFilter: (state, filter) => {
    state.filter = filter;
  },
  setFilters: (state, filters) => {
    state.filter = filters || {};
  },
  setKeyword: (state, keyword) => {
    state.keyword = keyword || '';
  },
  setDiscoveryPageId(state, id) {
    if (id == null || id === '') {
      state.discoveryPageId = null;
      return;
    }
    const s = String(id).trim();
    if (!s) {
      state.discoveryPageId = null;
      return;
    }
    const withoutPrefix = s.replace(/^d/i, '').trim();
    state.discoveryPageId = withoutPrefix || null;
  },
  setCachedKeyword: (state, keyword) => {
    state.cachedKeyword = keyword || null;
  },
  // no-results suggestions
  setSuggested(state, { type, value }) {
    state.suggested[type] = value;
  },
  setNoResultsStatus(state, statusId) {
    state.noResultsStatus = statusId;
  },
  setSimilarImageUrl(state, similarImageUrl) {
    state.similar.imageUrl = similarImageUrl;
  },
  setSimilarImageMeta(state, similarImageMetaObject) {
    state.similar.imageMeta = similarImageMetaObject;
  },
  setMobileFacetsActive: (state, bool) => {
    state.isMobileFacetsActive = bool;
  },
  setUploadProgress: (state, params) => {
    state.upload.status = params.status;

    if (params.progress) {
      state.upload.progress = params.progress;
    }

    if (params.message) {
      state.upload.message = params.message;
    }
  },
  setLoading: (state, bool) => {
    state.isLoading = bool;
  },
  setQueryId: (state, id) => {
    state.queryId = id;
  },
  //
  refreshToken(state, tokenName) {
    const index = state.tokens.findIndex(({ tokenName: id }) => id === tokenName);
    const token = axios.CancelToken.source();
    token.tokenName = tokenName;
    if (index > -1) { // cancel previous request
      state.tokens[index].cancel();
      state.tokens.splice(index, 1, token);
    } else {
      // create cancel token
      state.tokens.push(token);
    }
  },
  resetTokens(state) {
    state.tokens = [];
  },
  //
  setPrevNextIds(state) {
    state.elements.forEach((element, index) => {
      const prevIndex = index - 1;
      const nextIndex = index + 1;
      let prevElement = state.elements[prevIndex];
      let nextElement = state.elements[nextIndex];
      if (_.get(prevElement, 'object') === 'banner') {
        prevElement = state.elements[prevIndex - 1];
      }
      if (_.get(nextElement, 'object') === 'banner') {
        nextElement = state.elements[nextIndex + 1];
      }
      const prevElementId = _.get(prevElement, 'id', null);
      const nextElementId = _.get(nextElement, 'id', null);
      Vue.set(state.elements[index], 'prevProductId', prevElementId);
      Vue.set(state.elements[index], 'nextProductId', nextElementId);
    });
  },
  patchFilter(state, { key, value }) {
    Vue.set(state.filter, key, value);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
